import React, { Component } from 'react'
import format from 'date-fns/format'
import map from 'lodash/map'
import { Query, Mutation } from 'react-apollo'
import {
  CONTENTS_QUERY,
  DELETE_CONTENT,
  TAGS_QUERY,
  UPDATE_CONTENT_MUTATION,
} from 'graphql/queries'

import { Container, Header, Table, Button, Form } from 'semantic-ui-react'
import InfiniteScroll from 'react-infinite-scroller'
import Loader from 'components/Loader'
import Input from 'components/Input'
import './ContentList.css'

const WEB_HOST = process.env.WEB_HOST

// enum: updated | score | publishedTime
const ORDER = {
  LIST: 'updated,score',
  DEFAULT: 'score,updated',
}

function tagsToOptions({ id: value, name }) {
  return { text: name, name, value }
}

class ContentList extends Component {
  state = {
    offset: 0,
    limit: 50,
    q: '',
    order: ORDER.LIST,
    hasMore: true,
    query: {
      tags: ``,
    },

    selectedTags: [],
    tagIds: [],
    searchQuery: '',
  }

  handleSearch = event => {
    const order = event.target.value ? ORDER.DEFAULT : ORDER.LIST
    this.setState({ q: event.target.value, order })
  }

  handleTagChange = (event, { value: tagIds, options }) => {
    const selectedTags = tagIds.map(id => {
      const tag = options.find(o => o.value === id)
      return {
        id: tag.value,
        name: tag.name,
      }
    })
    this.setState({
      tagIds,
      selectedTags: selectedTags,
      searchQuery: '',
      query: {
        tags: String(map(selectedTags, ({ name }) => name)),
      },
    })
  }

  handleTagSearch = (event, { searchQuery }) => {
    this.setState({ searchQuery })
  }

  handleSearchFilter = options => options

  handleEditClick = event => {
    this.props.history.push(`/contents/edit/${event.target.id}`)
  }

  handleFilter = (_, { value }) => {
    this.setState({ q: value })
  }

  renderTable() {
    const { limit, offset, q, order, hasMore, query } = this.state
    const variables = { limit, offset, q, order, tags: query.tags }
    return (
      <Query query={CONTENTS_QUERY} variables={variables}>
        {({ data, loading, fetchMore }) => {
          if (loading) {
            return <Loader key="loader-key" />
          }

          return (
            <InfiniteScroll
              loadMore={() =>
                fetchMore({
                  variables: {
                    offset: data.contents ? data.contents.length : 0,
                  },
                  updateQuery: (prev, { fetchMoreResult, variables }) => {
                    if (!fetchMoreResult) return prev
                    if (!fetchMoreResult.contents.length) {
                      this.setState({ hasMore: false })
                    }
                    return {
                      ...prev,
                      contents: [...prev.contents, ...fetchMoreResult.contents],
                    }
                  },
                })
              }
              hasMore={hasMore}
              loader={<Loader key="loader-key" />}
            >
              <Table>
                <Table.Header>
                  <Table.Row>
                    <Table.HeaderCell width={4}>Title</Table.HeaderCell>
                    <Table.HeaderCell>Category</Table.HeaderCell>
                    <Table.HeaderCell>Content Type</Table.HeaderCell>
                    <Table.HeaderCell>Last Modified</Table.HeaderCell>
                    <Table.HeaderCell>Actions</Table.HeaderCell>
                    <Table.HeaderCell />
                  </Table.Row>
                </Table.Header>

                <Table.Body>{map(data.contents, this.renderRow)}</Table.Body>
              </Table>
            </InfiniteScroll>
          )
        }}
      </Query>
    )
  }

  renderRow = ({
    id,
    title,
    category,
    template,
    updated,
    publishedTime,
    lastModifiedBy,
  }) => {
    const { limit, offset, q } = this.state
    const variables = { limit, offset, q }
    const displayName = lastModifiedBy
      ? `${lastModifiedBy.firstName} ${lastModifiedBy.lastName}`
      : 'Unknown'
    return (
      <Table.Row key={id} className="hoverable">
        <Table.Cell>
          {publishedTime ? (
            <a
              target="_blank"
              rel="noopener noreferrer"
              href={`${WEB_HOST}/article/${id}`.replace('cms', 'ctt')}
            >
              {title}
            </a>
          ) : (
            <span title="Unpublished article">{title}</span>
          )}
        </Table.Cell>

        <Table.Cell>{category}</Table.Cell>
        <Table.Cell>{template}</Table.Cell>
        <Table.Cell>
          <span>{format(updated, 'MM/DD/YY, h:mm A')}</span>
          <br />
          <span>By {displayName}</span>
        </Table.Cell>

        <Table.Cell
          className="hoverable-target"
          style={{ whiteSpace: 'nowrap' }}
        >
          <Button id={id} primary onClick={this.handleEditClick}>
            Edit
          </Button>

          <Mutation
            mutation={UPDATE_CONTENT_MUTATION}
            update={cache => {
              const { contents } = cache.readQuery({
                query: CONTENTS_QUERY,
                variables,
              })

              const indexToUpdate = contents.findIndex(
                content => content.id === id,
              )
              const updatedContent = {
                ...contents[indexToUpdate],
                publishedTime: publishedTime ? null : Date.now(),
              }

              cache.writeQuery({
                query: CONTENTS_QUERY,
                variables,
                data: {
                  contents: [
                    ...contents.slice(0, indexToUpdate),
                    updatedContent,
                    ...contents.slice(indexToUpdate + 1),
                  ],
                },
              })
            }}
          >
            {(updateContent, { loading }) => {
              if (loading) {
                return (
                  <Button color="orange" disabled loading>
                    Updating...
                  </Button>
                )
              }

              return (
                <Button
                  color={publishedTime ? 'orange' : 'green'}
                  onClick={async () => {
                    await updateContent({
                      variables: {
                        id,
                        input: {
                          publishedTime: publishedTime ? null : Date.now(),
                        },
                      },
                    })
                  }}
                >
                  {publishedTime ? 'Unpublish' : 'Publish'}
                </Button>
              )
            }}
          </Mutation>
        </Table.Cell>
        <Table.Cell className="hoverable-target">
          <Mutation
            mutation={DELETE_CONTENT}
            update={cache => {
              const { contents } = cache.readQuery({
                query: CONTENTS_QUERY,
                variables,
              })
              cache.writeQuery({
                query: CONTENTS_QUERY,
                variables,
                data: {
                  contents: contents.filter(content => content.id !== id),
                },
              })
            }}
          >
            {(deleteContent, { loading }) => {
              if (loading) {
                return (
                  <Button negative disabled loading>
                    Deleting...
                  </Button>
                )
              }

              return (
                <Button
                  negative
                  onClick={() => {
                    const confirm = window.confirm(
                      `Are you sure you want to delete "${title}"`,
                    )
                    if (confirm) {
                      deleteContent({ variables: { id } })
                    }
                  }}
                >
                  Delete
                </Button>
              )
            }}
          </Mutation>
        </Table.Cell>
      </Table.Row>
    )
  }

  render() {
    return (
      <Container>
        <Header as="h1" content="Contents" />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '10px',
          }}
        >
          <div>
            <Input
              icon="search"
              placeholder="Search contents ..."
              onChange={this.handleSearch}
              minLength={2}
              debounceTimeout={300}
            />
          </div>

          <div style={{ display: 'flex' }}>
            <Button
              color="green"
              onClick={() => this.props.history.push('/contents/metrics')}
            >
              Content Metrics
            </Button>
            <Button
              primary
              onClick={() => this.props.history.push('/contents/new')}
            >
              Add Content
            </Button>
          </div>
        </div>
        <div style={{ marginBottom: '20px' }}>
          <Form>
            <Form.Group>
              <Query
                query={TAGS_QUERY}
                variables={{ q: this.state.searchQuery, limit: 100 }}
                context={{ debounceKey: 'tags', debounceTimeout: 100 }}
              >
                {({ data, loading }) => {
                  const selected = map(this.state.selectedTags, tagsToOptions)
                  const options = map(data.tags, tagsToOptions).concat(selected)

                  return (
                    <Form.Select
                      width={16}
                      required
                      placeholder="Filter by tags (result matches all terms)"
                      name="Select tags..."
                      multiple
                      loading={loading}
                      selection
                      options={options}
                      value={this.state.tagIds}
                      searchQuery={this.state.searchQuery}
                      search={this.handleSearchFilter}
                      onChange={this.handleTagChange}
                      onSearchChange={this.handleTagSearch}
                    />
                  )
                }}
              </Query>
            </Form.Group>
          </Form>
        </div>

        {this.renderTable()}
      </Container>
    )
  }
}

export default ContentList
